import api from './api'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

const searchService = {
  /**
   * Perform trust-weighted search (non-streaming)
   */
  search: async (query) => {
    const response = await api.post('/search', query)
    return response.data
  },

  /**
   * Perform trust-weighted search with SSE streaming
   * @param {Object} query - Search parameters
   * @param {number} query.profile_id - Profile ID
   * @param {string} query.keywords - Search keywords
   * @param {string} [query.scorable_type] - Type filter
   * @param {boolean} [query.age_restrict] - Age restriction flag
   * @param {number} [query.page=1] - Page number (25 results per page)
   * @param {Object} callbacks - Event callbacks
   * @param {Function} callbacks.onMetadata - Called with search metadata
   * @param {Function} callbacks.onResults - Called for each result batch
   * @param {Function} callbacks.onComplete - Called when search completes
   * @param {Function} callbacks.onError - Called on error
   * @returns {Function} Cleanup function to abort the stream
   */
  searchStream: (query, callbacks) => {
    const { onMetadata, onResults, onComplete, onError } = callbacks
    const token = localStorage.getItem('accessToken')

    // Build query string
    const params = new URLSearchParams({
      profile_id: query.profile_id,
      keywords: query.keywords,
      page: query.page || 1,
    })

    if (query.scorable_type) {
      params.append('scorable_type', query.scorable_type)
    }
    if (query.scorable_types && query.scorable_types.length > 0) {
      query.scorable_types.forEach(type => {
        params.append('scorable_types', type)
      })
    }
    if (query.age_restrict !== undefined) {
      params.append('age_restrict', query.age_restrict)
    }

    const url = `${API_URL}/api/search/stream?${params.toString()}`

    // Create EventSource with auth header via fetch
    const controller = new AbortController()

    fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      signal: controller.signal,
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        const processEvents = (text) => {
          buffer += text
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          let currentEvent = null
          let currentData = ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6)
            } else if (line === '' && currentEvent && currentData) {
              // End of event, process it
              try {
                const data = JSON.parse(currentData)

                switch (currentEvent) {
                  case 'metadata':
                    onMetadata?.(data)
                    break
                  case 'results':
                    onResults?.(data)
                    break
                  case 'complete':
                    onComplete?.(data)
                    break
                  case 'error':
                    onError?.(new Error(data.message))
                    break
                }
              } catch (e) {
                console.error('Failed to parse SSE data:', e)
              }

              currentEvent = null
              currentData = ''
            }
          }
        }

        const read = () => {
          reader.read().then(({ done, value }) => {
            if (done) {
              // Process any remaining buffer
              if (buffer) {
                processEvents('\n')
              }
              return
            }

            processEvents(decoder.decode(value, { stream: true }))
            read()
          }).catch(err => {
            if (err.name !== 'AbortError') {
              onError?.(err)
            }
          })
        }

        read()
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          onError?.(err)
        }
      })

    // Return cleanup function
    return () => controller.abort()
  },

  /**
   * Get search history
   */
  getSearchHistory: async (profileId, limit = 20) => {
    const response = await api.get('/search/history', {
      params: { profile_id: profileId, limit }
    })
    return response.data
  },
}

export default searchService
